import React from 'react';
import {
  IconLocationPin,
  IconHome,
  IconBuilding,
  IconLaundryRoom,
  IconLocation,
  IconUser,
  IconWasherDryer,
  IconDishwasher,
  IconElevator,
  IconGym,
  IconOutdoorSpace,
  IconPetsAllowed,
  IconAc,
  IconWaterTap,
  IconParking,
  IconPool,
  IconDoorman,
} from '@repo/svgs';
import type { AmenitiesState, CompliancesState } from '@repo/store';

// Amenity configuration types
export type AmenityConfig = {
  key: keyof AmenitiesState;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  category: 'unit' | 'building' | 'policy';
};

// Compliance configuration types
export type ComplianceConfig = {
  key: keyof CompliancesState;
  label: string;
};

// Bathroom configuration (similar to bedrooms)
export const BATHROOM_VALUES = [1, 2, 3, 4] as const;
export type BathroomValue = (typeof BATHROOM_VALUES)[number];

export function bathroomLabelFor(value: BathroomValue): string {
  return value === 4 ? '4+' : `${value}+`;
}

// Amenities configuration - data-driven rendering
export const AMENITIES_CONFIG: AmenityConfig[] = [
  // Unit amenities (Selection pills with icons)
  { key: 'isUnitWasherDryer', label: 'Washer/dryer', icon: IconWasherDryer, category: 'unit' },
  { key: 'isUnitDishwasher', label: 'Dishwasher', icon: IconDishwasher, category: 'unit' },
  { key: 'isUnitAirConditioned', label: 'A/C', icon: IconAc, category: 'unit' },
  {
    key: 'isUnitPrivateOutdoor',
    label: 'Private outdoor',
    icon: IconOutdoorSpace,
    category: 'unit',
  },

  // Building amenities (Selection pills with icons)
  {
    key: 'isBuildingLaundryRoom',
    label: 'Laundry room',
    icon: IconLaundryRoom,
    category: 'building',
  },
  { key: 'isBuildingElevator', label: 'Elevator', icon: IconElevator, category: 'building' },
  { key: 'isBuildingDoorman', label: 'Doorman', icon: IconDoorman, category: 'building' },
  { key: 'isBuildingGym', label: 'Gym', icon: IconGym, category: 'building' },
  { key: 'isBuildingParking', label: 'Parking', icon: IconParking, category: 'building' },
  {
    key: 'isBuildingOutdoor',
    label: 'Outdoor space',
    icon: IconOutdoorSpace,
    category: 'building',
  },
  { key: 'isBuildingPool', label: 'Pool', icon: IconPool, category: 'building' },

  // Policy (ListCell with checkbox)
  { key: 'isUnitPetsAllowed', label: 'Pets allowed', icon: IconPetsAllowed, category: 'policy' },
];

// NYC Government Data / Compliances configuration
export const COMPLIANCES_CONFIG: ComplianceConfig[] = [
  { key: 'hasNoBuildingOneYearBedbugHistory', label: 'No bedbugs in the past year' },
  { key: 'hasNoBuildingEvictionHistory', label: 'No eviction history' },
  { key: 'hasNoBuildingPendingLitigation', label: 'No pending litigation' },
  { key: 'hasNoBuildingOpenViolations', label: 'No open violations' },
];

// Helper functions to get filtered configs by category
export const getUnitAmenities = () => AMENITIES_CONFIG.filter(a => a.category === 'unit');
export const getBuildingAmenities = () => AMENITIES_CONFIG.filter(a => a.category === 'building');
export const getPolicyAmenities = () => AMENITIES_CONFIG.filter(a => a.category === 'policy');
